import type { PreOrderFlowChange, ModifyBatchInput } from "@stockline/web";
import type { PreOrderFlow as OfflinePreOrderFlow } from "@stockline/shared/types/orders/pre-order-flow";
import type { PreOrderFlow as LegacyPreOrderFlow } from "@/app/api/types/preOrder";
import type { ModifyBufferSlice } from "../stores/modifyBufferStore";

/**
 * Buffer-only marker used to distinguish locally-added rows from persisted ones.
 * NEVER serialize this to the server — it's stripped in `toOfflineFlowData` and
 * is a frontend implementation detail only.
 */
export const NEW_FLOW_MARKER = "__newFlow" as const;

const SNAKE_TO_CAMEL: Record<string, keyof OfflinePreOrderFlow> = {
  product_id: "productId",
  partner_id: "partnerId",
  unit_id: "unitId",
  quantity: "quantity",
  weight: "weight",
  price_per_weight: "pricePerWeight",
  comment: "comment",
  hidden_comment: "hiddenComment",
  status: "status",
  index: "flowIndex",
  delivery_date: "deliveryDate",
  order_date: "orderDate",
  flow_metadata: "flowMetadata",
  created_at: "createdAt",
};

/** Strip the frontend-only __newFlow marker; collapse to null if that was the
 * only key. Buffer-side metadata can include the marker; server-side never. */
export function stripBufferMarkers(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!metadata) return null;
  if (!(NEW_FLOW_MARKER in metadata)) return metadata;
  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (key !== NEW_FLOW_MARKER) rest[key] = value;
  }
  return Object.keys(rest).length === 0 ? null : rest;
}

/**
 * Translate a snake-case buffer patch (legacy `PreOrderFlow` shape rendered
 * by recap cells) into the camel-case offline-flow shape expected by the
 * V2 push-batch endpoint. Skips undefined fields and runs `flow_metadata`
 * through `stripBufferMarkers` so the `__newFlow` sentinel never leaves
 * the client.
 */
export function toOfflineFlowData(
  partial: Partial<LegacyPreOrderFlow>,
): Partial<OfflinePreOrderFlow> {
  const out: Partial<OfflinePreOrderFlow> = {};
  const source = partial as Record<string, unknown>;
  for (const [snake, camel] of Object.entries(SNAKE_TO_CAMEL)) {
    if (!Object.hasOwn(source, snake)) continue;
    const v = source[snake];
    if (v === undefined) continue;
    if (snake === "flow_metadata") {
      (out as Record<string, unknown>)[camel] = stripBufferMarkers(
        v as Record<string, unknown> | null,
      );
    } else {
      (out as Record<string, unknown>)[camel] = v;
    }
  }
  return out;
}

/**
 * Build the atomic `ModifyBatchInput` consumed by `usePreOrderModifyBatch`
 * from a single pre-order's buffer slice. Order: updates, additions,
 * deletions — matches the order the server applies them inside the
 * push-batch transaction.
 */
export function buildModifyBatchInput(
  preOrderId: string,
  slice: ModifyBufferSlice,
): ModifyBatchInput {
  const flowChanges: PreOrderFlowChange[] = [];
  for (const [flowId, patch] of Object.entries(slice.edits)) {
    flowChanges.push({
      op: "update",
      id: flowId,
      data: toOfflineFlowData(patch),
    });
  }
  for (const flow of slice.added) {
    flowChanges.push({
      op: "create",
      id: flow.id,
      data: { ...toOfflineFlowData(flow), preOrderId },
    });
  }
  for (const flowId of slice.deleted) {
    flowChanges.push({ op: "delete", id: flowId });
  }
  return { preOrderId, flowChanges };
}
